import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';

function decodeJwtPayload(jwt) {
  const parts = jwt.split('.');
  if (parts.length !== 3) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

/**
 * Fetches a chat token from the Plone @chat-token POST endpoint.
 * Authenticated users get a token immediately; anonymous users receive
 * needsNickname=true on first call, then provide a nickname for their token.
 *
 * Token refresh stores the new password for future reconnects but does NOT
 * change the returned token object reference (to avoid tearing down an
 * active Strophe connection).
 */
export function useChatToken(contentPath) {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [needsNickname, setNeedsNickname] = useState(false);
  const refreshTimer = useRef(null);
  const latestPasswordRef = useRef(null);
  const authToken = useSelector((state) => state.userSession?.token);

  const fetchToken = useCallback(
    async (nickname, isRefresh = false) => {
      if (!isRefresh) {
        setLoading(true);
        setError(null);
      }

      const tokenPath =
        contentPath === '/' || contentPath === ''
          ? '/++api++/@chat-token'
          : `/++api++${contentPath}/@chat-token`;

      try {
        const headers = { 'Content-Type': 'application/json' };
        if (authToken) {
          headers['Authorization'] = `Bearer ${authToken}`;
        }
        const opts = { credentials: 'same-origin', headers };
        let resp;

        // Always POST — the unified endpoint handles both authenticated
        // (ignores body) and anonymous (reads nickname from body).
        if (nickname) {
          resp = await fetch(tokenPath, {
            ...opts,
            method: 'POST',
            body: JSON.stringify({ nickname }),
          });
        } else {
          resp = await fetch(tokenPath, {
            ...opts,
            method: 'POST',
            body: JSON.stringify({}),
          });
        }

        if (resp.status === 401) {
          setNeedsNickname(true);
          setLoading(false);
          return;
        }

        if (!resp.ok) {
          const body = await resp.json().catch(() => ({}));
          if (!isRefresh) {
            setError(body.error || 'Failed to fetch chat token');
            setLoading(false);
          }
          return;
        }

        const data = await resp.json();
        latestPasswordRef.current = data.password;

        if (isRefresh) {
          // Silent refresh: only update the stored password for future reconnects
          // Do NOT call setToken — this avoids tearing down the active connection
        } else {
          setToken(data);
          setNeedsNickname(false);
          setLoading(false);
        }

        // Schedule next refresh 5 min before JWT expiry
        if (data.password) {
          const payload = decodeJwtPayload(data.password);
          if (payload?.exp) {
            const expiresIn = payload.exp * 1000 - Date.now() - 5 * 60 * 1000;
            if (expiresIn > 0) {
              if (refreshTimer.current) clearTimeout(refreshTimer.current);
              refreshTimer.current = setTimeout(
                () => fetchToken(nickname, true),
                expiresIn,
              );
            }
          }
        }
      } catch (err) {
        if (!isRefresh) {
          setError(err.message || 'Network error');
          setLoading(false);
        }
      }
    },
    [contentPath, authToken],
  );

  useEffect(() => {
    fetchToken();
    return () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    };
  }, [fetchToken]);

  const postNickname = useCallback(
    (nickname) => {
      setNeedsNickname(false);
      fetchToken(nickname);
    },
    [fetchToken],
  );

  const retryToken = useCallback(() => {
    fetchToken();
  }, [fetchToken]);

  return {
    token,
    loading,
    error,
    needsNickname,
    postNickname,
    retryToken,
    latestPasswordRef,
  };
}
